import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Paperclip, Send, X, FileText } from 'lucide-react';
import { supportApi } from '../../lib/api';

interface SupportModalProps {
    open: boolean;
    onClose: () => void;
    canOpen: boolean;
    onUnreadCountChanged?: () => void;
}

export function SupportModal({ open, onClose, canOpen, onUnreadCountChanged }: SupportModalProps) {
    const [loading, setLoading] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [ticket, setTicket] = React.useState<any | null>(null);
    const [messages, setMessages] = React.useState<any[]>([]);
    const [text, setText] = React.useState('');
    const [file, setFile] = React.useState<File | null>(null);
    const [error, setError] = React.useState('');
    const endRef = React.useRef<HTMLDivElement | null>(null);

    const loadThread = React.useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await supportApi.getOpenTicket();
            setTicket(data.ticket || null);
            const msgList = Array.isArray(data.messages) ? data.messages : [];
            setMessages(msgList.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
            setError('');
        } catch (e: any) {
            setError(e?.message || 'Erreur chargement support.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Messages reçus de la plateforme : une liste, puis le fil choisi (répondre seulement si le support l'a permis).
    const [vue, setVue] = React.useState<'conversation' | 'messages'>('conversation');
    const [inbox, setInbox] = React.useState<any[]>([]);
    const [fil, setFil] = React.useState<{ ticket: any; messages: any[] } | null>(null);

    const loadInbox = React.useCallback(async () => {
        try {
            const data = await supportApi.getInbox();
            const liste = Array.isArray(data) ? data : [];
            setInbox(liste);
            return liste;
        } catch {
            return [];
        }
    }, []);

    const ouvrirFil = React.useCallback(async (id: string) => {
        try {
            setError('');
            const data = await supportApi.getInboxThread(id);
            setFil({ ticket: data.ticket, messages: Array.isArray(data.messages) ? data.messages : [] });
            await loadInbox();
            if (onUnreadCountChanged) onUnreadCountChanged();
        } catch (e: any) {
            setError(e?.message || 'Erreur chargement du message.');
        }
    }, [loadInbox, onUnreadCountChanged]);

    React.useEffect(() => {
        if (open && canOpen) {
            setError('');
            setFil(null);
            loadThread();
            loadInbox().then((liste) => setVue(liste.some((m: any) => m.non_lus > 0) ? 'messages' : 'conversation'));
        } else if (!open) {
            setError('');
        }
    }, [open, canOpen, loadThread, loadInbox]);

    const nonLusInbox = inbox.reduce((somme, m) => somme + (m.non_lus || 0), 0);

    React.useEffect(() => {
        if (open && messages.length > 0) {
            const unreadAdminMsgIds = messages
                .filter(m => m.sender_type === 'admin' && !m.is_read)
                .map(m => m.id);
            if (unreadAdminMsgIds.length > 0) {
                supportApi.markAsRead(unreadAdminMsgIds).then(() => {
                    if (onUnreadCountChanged) onUnreadCountChanged();
                });
            }
        }
    }, [open, messages, onUnreadCountChanged]);

    React.useEffect(() => {
        if (open) {
            endRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, open]);

    // Auto-clear error after 5 seconds
    React.useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const sendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!text.trim() && !file) return;
        try {
            setSending(true);
            setError('');

            let fileUrl: string | null = null;
            let fileName: string | null = null;

            if (file) {
                const formData = new FormData();
                formData.append('file', file);
                const upload = await supportApi.uploadFile(formData);
                fileUrl = upload.file_url;
                fileName = upload.file_name || file.name;
            }

            await supportApi.sendClientMessage({
                ticket_id: vue === 'messages' ? fil?.ticket?.id : ticket?.id,
                message: text.trim() || null,
                file_url: fileUrl,
                file_name: fileName,
            });

            setText('');
            setFile(null);
            setError('');
            if (vue === 'messages' && fil?.ticket?.id) await ouvrirFil(fil.ticket.id);
            else await loadThread();
            if (onUnreadCountChanged) onUnreadCountChanged();
        } catch (e: any) {
            setError(e?.message || 'Erreur envoi message.');
        } finally {
            setSending(false);
        }
    };

    const isImage = (url?: string) => {
        const u = (url || '').toLowerCase();
        return u.endsWith('.png') || u.endsWith('.jpg') || u.endsWith('.jpeg') || u.endsWith('.gif') || u.endsWith('.webp') || u.endsWith('.svg');
    };

    const resolveUrl = (url: string) => (url.startsWith('http') ? url : `${window.location.origin}${url}`);

    const enListe = vue === 'messages' && !fil;
    const messagesAffiches = vue === 'messages' ? (fil?.messages || []) : messages;
    const reponseAutorisee = vue === 'conversation' || !!fil?.ticket?.reply_allowed;

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4 transition-colors duration-200">
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.96 }}
                        transition={{ duration: 0.22 }}
                        className="w-full max-w-3xl max-h-[90vh] bg-white dark:bg-surface border border-border rounded-2xl overflow-hidden flex flex-col transition-colors duration-200"
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-surface-soft transition-colors duration-200">
                            <div>
                                <h3 className="text-slate-900 dark:text-content text-lg font-bold">Support L'IAmani</h3>
                                <p className="text-xs text-slate-500 dark:text-content-muted">Conversation en cours</p>
                            </div>
                            <button onClick={onClose} className="p-2 rounded-lg text-slate-500 dark:text-content-muted hover:text-slate-900 dark:hover:text-slate-900 hover:bg-slate-200 dark:hover:bg-surface-soft transition-colors duration-200">
                                <X size={18} />
                            </button>
                        </div>

                        {canOpen && (
                            <div className="flex gap-1 px-4 pt-3 border-b border-border">
                                {([['conversation', 'Ma conversation', 0], ['messages', "Messages de L'IAmani", nonLusInbox]] as const).map(([cle, libelle, nb]) => (
                                    <button
                                        key={cle}
                                        type="button"
                                        onClick={() => { setVue(cle); setFil(null); }}
                                        className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${vue === cle ? 'border-brand-500 text-slate-900 dark:text-content' : 'border-transparent text-slate-500 dark:text-content-muted hover:text-slate-900 dark:hover:text-content'}`}
                                    >
                                        {libelle}
                                        {nb > 0 && <span className="ml-2 min-w-5 h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">{nb}</span>}
                                    </button>
                                ))}
                            </div>
                        )}

                        {!canOpen ? (
                            <div className="flex-1 flex items-center justify-center text-slate-500 dark:text-content-muted">Support indisponible</div>
                        ) : (
                            <>
                                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                    {loading && <div className="text-slate-500 dark:text-content-muted text-sm">Chargement...</div>}
                                    {error && <div className="text-red-400 text-sm">{error}</div>}
                                    {vue === 'conversation' && !loading && messages.length === 0 && (
                                        <div className="text-center text-slate-500 dark:text-content-muted py-10">Aucun message. Démarrez une nouvelle conversation.</div>
                                    )}
                                    {enListe && inbox.length === 0 && (
                                        <div className="text-center text-slate-500 dark:text-content-muted py-10">Aucun message de L'IAmani pour le moment.</div>
                                    )}
                                    {enListe && inbox.map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => ouvrirFil(m.id)}
                                            className="w-full text-left rounded-xl border border-border bg-surface-soft p-3 hover:border-brand-500/50 transition-colors"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={`text-sm truncate ${m.non_lus > 0 ? 'font-bold' : 'font-semibold'} text-slate-900 dark:text-content`}>{m.subject}</p>
                                                <span className="text-[10px] text-slate-500 dark:text-content-muted shrink-0">{new Date(m.created_at).toLocaleDateString('fr-FR')}</span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-content-muted line-clamp-2 mt-1">{m.apercu}</p>
                                            <p className="text-[11px] mt-1.5 text-slate-500 dark:text-content-muted">
                                                {m.non_lus > 0 && <span className="text-red-500 font-bold mr-2">Nouveau</span>}
                                                {m.reply_allowed ? 'Vous pouvez répondre' : "Pas de réponse attendue"}
                                            </p>
                                        </button>
                                    ))}
                                    {vue === 'messages' && fil && (
                                        <button type="button" onClick={() => setFil(null)} className="text-xs font-semibold text-brand-600 hover:underline">
                                            ← Tous les messages
                                        </button>
                                    )}
                                    {vue === 'messages' && fil && (
                                        <p className="text-base font-bold text-slate-900 dark:text-content">{fil.ticket?.subject}</p>
                                    )}

                                    {messagesAffiches.map((m) => {
                                        const isFromAdmin = m.sender_type === 'admin';
                                        return (
                                            <div key={m.id} className={`flex ${isFromAdmin ? 'justify-start' : 'justify-end'}`}>
                                                <div className={`max-w-[78%] rounded-2xl px-4 py-3 border ${
                                                    isFromAdmin 
                                                        ? 'bg-white dark:bg-surface border-warning-400/40 dark:border-[#C8AA6E]/50' 
                                                        : 'bg-surface-soft border-gray-300 dark:border-border'
                                                } text-slate-900 dark:text-content transition-colors duration-200`}>
                                                    {m.message && <p className="text-sm whitespace-pre-wrap">{m.message}</p>}

                                                    {m.file_url && (
                                                        <a
                                                            href={resolveUrl(m.file_url)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="block mt-2 rounded-lg border border-gray-300 dark:border-border p-2 hover:bg-slate-100 dark:hover:bg-surface-soft transition-colors duration-200"
                                                        >
                                                            {isImage(m.file_url) ? (
                                                                <img src={resolveUrl(m.file_url)} alt={m.file_name || 'fichier'} className="max-h-40 rounded-md object-contain mb-2" />
                                                            ) : (
                                                                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-content-muted">
                                                                    <FileText size={16} />
                                                                    <span>{m.file_name || 'Télécharger la pièce jointe'}</span>
                                                                </div>
                                                            )}
                                                            {isImage(m.file_url) && (
                                                                <p className="text-xs text-slate-500 dark:text-content-muted">{m.file_name || 'Pièce jointe image'}</p>
                                                            )}
                                                        </a>
                                                    )}

                                                    <p className="mt-1 text-[10px] text-slate-500 dark:text-content-muted">{new Date(m.created_at).toLocaleString()}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={endRef} />
                                </div>

                                {vue === 'messages' && fil && !reponseAutorisee && (
                                    <div className="p-4 border-t border-border bg-surface-soft text-center text-sm text-slate-500 dark:text-content-muted">
                                        Ce message est une information : il n'attend pas de réponse.
                                    </div>
                                )}
                                {!enListe && reponseAutorisee && (
                                <form onSubmit={sendMessage} className="p-4 border-t border-border bg-surface-soft transition-colors duration-200">
                                    <div className="flex items-end gap-2">
                                        <label className="p-2 rounded-lg border border-gray-300 dark:border-border text-slate-600 dark:text-content-muted hover:bg-slate-200 dark:hover:bg-surface-soft cursor-pointer transition-colors duration-200">
                                            <Paperclip size={18} />
                                            <input
                                                type="file"
                                                className="hidden"
                                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                            />
                                        </label>
                                        <textarea
                                            value={text}
                                            onChange={(e) => setText(e.target.value)}
                                            rows={2}
                                            placeholder="Votre message..."
                                            className="flex-1 resize-none px-3 py-2 rounded-lg bg-surface border border-gray-300 dark:border-border text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                        />
                                        <button
                                            type="submit"
                                            disabled={sending || (!text.trim() && !file)}
                                            className="px-3 py-2 rounded-lg bg-white text-slate-900 font-bold disabled:opacity-50"
                                        >
                                            <Send size={18} />
                                        </button>
                                    </div>
                                    {file && <p className="text-xs text-slate-500 dark:text-content-muted mt-2">Pièce jointe: {file.name}</p>}
                                    {ticket && <p className="text-[10px] text-slate-600 dark:text-gray-600 mt-1">Ticket: {ticket.id}</p>}
                                </form>
                                )}
                            </>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
